// The device's local database: a thin promise layer over IndexedDB, no
// library. One database per device holds every account that signed in on
// it until logout deletes the whole database.
//
// Stores:
//   runs     LocalRun, key [accountId, assessmentId], index byAccount
//   profiles Profile, key accountId
//   meta     device facts, out-of-line keys ("deviceId")

import { ASSESSMENT_IDS } from "../schema/types";
import type {
  AccountRunKey,
  AssessmentId,
  AssessmentRun,
  FinishRunRequest,
  Profile,
  SaveAnswersRequest,
  StartRedoRequest,
} from "./types";

export const DB_NAME = "poweryourflow";
const DB_VERSION = 1;

export const RUNS = "runs";
export const PROFILES = "profiles";
export const META = "meta";
const BY_ACCOUNT = "byAccount";
const DEVICE_ID = "deviceId";

/**
 * One call the device still owes the server. `rev` is the run's revision
 * when the operation was last written: a save replaced while in flight
 * gets a new rev, so the syncer knows its reply is already stale.
 */
export type PendingOp =
  | { kind: "save"; rev: number; request: SaveAnswersRequest }
  | { kind: "finish"; rev: number; request: FinishRunRequest }
  | { kind: "redo"; rev: number; request: StartRedoRequest };

/** A run as the device holds it: the local view plus what is unsent. */
export interface LocalRun {
  accountId: string;
  assessmentId: AssessmentId;
  /** What the hub shows: the server run, or the local one ahead of it. */
  run: AssessmentRun;
  /** Bumped on every local change; a server reply never touches it. */
  revision: number;
  /** In order. Empty when the server holds everything the device knows. */
  outbox: PendingOp[];
  /** Operations the server refused for good, kept on the device, never resent. */
  refused?: { code: string; at: number; ops: PendingOp[] };
}

export function openDatabase(factory: IDBFactory, name: string = DB_NAME): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(name, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      const runs = db.createObjectStore(RUNS, { keyPath: ["accountId", "assessmentId"] });
      runs.createIndex(BY_ACCOUNT, "accountId");
      db.createObjectStore(PROFILES, { keyPath: "accountId" });
      db.createObjectStore(META);
    };
    request.onsuccess = () => {
      const db = request.result;
      // Another tab logging out deletes the database; let it.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    request.onerror = () => reject(request.error ?? new Error(`cannot open ${name}`));
    request.onblocked = () => reject(new Error(`${name} is blocked by an older connection`));
  });
}

/** Deletes the whole database, every account in it. */
export function deleteDatabase(factory: IDBFactory, name: string = DB_NAME): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = factory.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error(`cannot delete ${name}`));
  });
}

/** Resolves with the request's result. */
export function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

/**
 * Runs `work` in one transaction and resolves with its result once the
 * transaction has committed. `work` may await IndexedDB requests of `tx`
 * and nothing else: any other await lets the transaction commit early.
 */
export function inTransaction<T>(
  db: IDBDatabase,
  stores: string[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction) => Promise<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(stores, mode);
    let result: T;
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
    work(tx).then(
      (value) => {
        result = value;
      },
      (error: unknown) => {
        try {
          tx.abort();
        } catch {
          // Already finished; the error below is the one that matters.
        }
        reject(error);
      },
    );
  });
}

export const runKey = (accountId: string, assessmentId: AssessmentId): AccountRunKey => [accountId, assessmentId];

export async function getRun(tx: IDBTransaction, accountId: string, assessmentId: AssessmentId): Promise<LocalRun | undefined> {
  return (await done(tx.objectStore(RUNS).get(runKey(accountId, assessmentId)))) as LocalRun | undefined;
}

/** The account's runs. An account holds one per assessment, so that is the bound. */
export async function getAccountRuns(tx: IDBTransaction, accountId: string): Promise<LocalRun[]> {
  return (await done(tx.objectStore(RUNS).index(BY_ACCOUNT).getAll(accountId, ASSESSMENT_IDS.length))) as LocalRun[];
}

export async function getProfile(tx: IDBTransaction, accountId: string): Promise<Profile | undefined> {
  return (await done(tx.objectStore(PROFILES).get(accountId))) as Profile | undefined;
}

/** This device's id, made once with crypto.randomUUID and kept until logout. */
export async function deviceId(tx: IDBTransaction): Promise<string> {
  const meta = tx.objectStore(META);
  const known = (await done(meta.get(DEVICE_ID))) as string | undefined;
  if (known) return known;
  const made = crypto.randomUUID();
  meta.put(made, DEVICE_ID);
  return made;
}
